import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const AboutContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#0B0813',
  padding: '40px 20px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '30px',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
}));

const ContentCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(11, 8, 19, 0.8)',
  border: '1px solid #00F0FF',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  marginBottom: '20px',
}));

const ContentText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  lineHeight: '1.8',
  marginBottom: '15px',
}));

const HighlightText = styled(Typography)(({ theme }) => ({
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  fontWeight: 'bold',
}));

const About: React.FC = () => {
  return (
    <AboutContainer>
      <Container maxWidth="lg">
        <SectionTitle>About StegSafe</SectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <ContentText>
                  <HighlightText>// WHAT IS STEGSAFE?</HighlightText>
                </ContentText>
                <ContentText>
                  StegSafe is an advanced image steganography tool that allows you to hide secret messages inside images using sophisticated pixel-based encoding techniques. Our platform combines military-grade encryption with cutting-edge steganography to provide unparalleled security for your sensitive data.
                </ContentText>
                <ContentText>
                  Unlike traditional encryption methods that only protect data at rest, StegSafe hides your information in plain sight, making it virtually undetectable to unauthorized observers while maintaining the visual integrity of your images.
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <ContentText>
                  <HighlightText>// ENCRYPTION METHOD</HighlightText>
                </ContentText>
                <ContentText>
                  We employ AES-256-GCM encryption, the same standard used by governments and financial institutions worldwide. Your messages are encrypted before being embedded into images, ensuring dual-layer protection:
                </ContentText>
                <ContentText>
                  • First layer: AES-256-GCM encryption with PBKDF2 key derivation<br/>
                  • Second layer: LSB (Least Significant Bit) steganography embedding<br/>
                  • Third layer: Multi-bit depth encoding options (1, 2, or 4 bits)
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <ContentText>
                  <HighlightText>// PRIVACY DESIGN</HighlightText>
                </ContentText>
                <ContentText>
                  Your privacy is our core principle. StegSafe is designed with a zero-knowledge architecture:
                </ContentText>
                <ContentText>
                  • No images are stored on our servers<br/>
                  • All processing happens in real-time<br/>
                  • Encrypted data is never logged or cached<br/>
                  • Open-source transparency for security verification<br/>
                  • No account registration required
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <ContentText>
                  <HighlightText>// TECHNOLOGY STACK</HighlightText>
                </ContentText>
                <ContentText>
                  Built with modern web technologies for optimal performance and security:
                </ContentText>
                <ContentText>
                  • Frontend: React with TypeScript<br/>
                  • Backend: Python Flask with optimized memory management<br/>
                  • Encryption: PyCryptodome (AES-256-GCM)<br/>
                  • Image Processing: PIL and NumPy<br/>
                  • Analytics: Microsoft Clarity<br/>
                  • Deployment: Render.com
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>
        </Grid>
      </Container>
    </AboutContainer>
  );
};

export default About;
